/**
 * UniverseOutlineList: the IN ORBIT list beside the stage. Subject rows open
 * into their groups (and, for competitors, their rivals) and then their facts.
 *
 * It used to carry a maturity score and a progress bar above the rows. Those
 * are gone: the page's status sentence and next move say what the score was
 * trying to say, from the same arcs the orbit draws.
 */
import type { CSSProperties } from "react";
import { BookOpen, Building2, ChevronRight } from "lucide-react";
import {
  countOutlineFacts,
  type UniverseOutlineGroup,
} from "../data/universeOutline";
import { stripFactPrefix } from "../data/universeText";

/** The fact card's id, so a fact row can say which region it controls. */
export const UNIVERSE_FACT_CARD_ID = "co-universe-fact-card";

type GroupRowProps = {
  g: UniverseOutlineGroup;
  /** subject -> named competitor -> facet is intentionally three levels. */
  depth: number;
  /** False when an ancestor disclosure is closed; keeps clipped rows untabbable. */
  revealed: boolean;
  openGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  activeFactId: string | null;
  selectedFactId: string | null;
  onSelectFact: (id: string) => void;
  onHoverFact: (id: string | null) => void;
  /** The subject whose ring is lit on the stage. Its row lights here too. */
  litSubjectKey: string | null;
  /** Hovering or focusing a subject row lights that ring on the stage. */
  onHoverSubject: (subjectKey: string | null) => void;
  /** The other groups a fact is filed in, read from the group `groupKey`. */
  alsoIn: (factId: string, groupKey: string) => string;
  /** Position among its siblings, for the open/arrival stagger. */
  index: number;
  /** Top-level rows only: how the row arrives with the page. */
  arrival?: string;
};

/**
 * One row of the universe outline. Most subjects unfold directly into facets;
 * multi-instance subjects add the named entity level in between.
 *
 * A `planned` row is a group the backend has no concept for yet. It renders as
 * stated intent: no "add facts" affordance, because a fact literally cannot be
 * filed there.
 */
function GroupRow({
  g,
  depth,
  revealed,
  openGroups,
  onToggleGroup,
  activeFactId,
  selectedFactId,
  onSelectFact,
  onHoverFact,
  litSubjectKey,
  onHoverSubject,
  alsoIn,
  index,
  arrival,
}: GroupRowProps) {
  const kids = g.children ?? [];
  const open = openGroups.has(g.key);
  const descendantsRevealed = revealed && open;
  const count = countOutlineFacts(g);
  const empty = count === 0;
  const isEntity = g.kind === "entity" || g.kind === "general";
  const subjectKey = depth === 0 ? g.subjectKey : undefined;
  const lit = !!subjectKey && subjectKey === litSubjectKey;
  const tooltip = g.planned
    ? `${g.label}: not built yet. No concept exists for this group, so no fact can be filed here until the backend adds one.`
    : `${g.label}: ${count} fact${count === 1 ? "" : "s"}${empty ? ", nothing charted yet" : ""}`;

  return (
    <div
      key={g.key}
      className={depth > 0 ? "co-orbit-group co-orbit-sub" : "co-orbit-group"}
      data-kind={g.kind}
      data-open={open || undefined}
      data-shown={revealed || undefined}
      style={{ "--row-i": index, ...(arrival ? { animation: arrival } : {}) } as CSSProperties}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-hidden={revealed ? undefined : true}
        tabIndex={revealed ? undefined : -1}
        aria-label={tooltip}
        onClick={() => onToggleGroup(g.key)}
        onMouseEnter={subjectKey ? () => onHoverSubject(subjectKey) : undefined}
        onMouseLeave={subjectKey ? () => onHoverSubject(null) : undefined}
        onFocus={subjectKey ? () => onHoverSubject(subjectKey) : undefined}
        onBlur={subjectKey ? () => onHoverSubject(null) : undefined}
        className="co-orbit-group-btn"
        data-kind={g.kind}
        data-planned={g.planned || undefined}
        data-lit={lit || undefined}
        title={tooltip}
        style={{ "--outline-depth": depth } as CSSProperties}
      >
        <span
          aria-hidden
          className="co-orbit-caret"
          style={{ transform: open ? "rotate(90deg)" : "none" }}
        >
          <ChevronRight size={11} strokeWidth={2} />
        </span>
        {isEntity ? (
          <span className="co-orbit-entity-mark" aria-hidden>
            {g.kind === "general" ? (
              <BookOpen size={13} strokeWidth={1.8} />
            ) : (
              <Building2 size={13} strokeWidth={1.8} />
            )}
          </span>
        ) : null}
        <span className="co-orbit-label-stack">
          <span className="co-orbit-group-label">{g.label}</span>
          {isEntity && open && g.blurb ? (
            <span className="co-orbit-group-blurb">{g.blurb}</span>
          ) : null}
        </span>
        <span className="co-orbit-group-count" data-empty={empty || undefined}>
          {count}
        </span>
      </button>
      <div className="co-orbit-fold" data-open={open}>
        <div className="co-orbit-fold-inner">
          <div className="co-orbit-fold-content">
            {kids.map((child, ci) => (
              <GroupRow
                key={child.key}
                index={ci}
                g={child}
                depth={depth + 1}
                revealed={descendantsRevealed}
                openGroups={openGroups}
                onToggleGroup={onToggleGroup}
                activeFactId={activeFactId}
                selectedFactId={selectedFactId}
                onSelectFact={onSelectFact}
                onHoverFact={onHoverFact}
                litSubjectKey={litSubjectKey}
                onHoverSubject={onHoverSubject}
                alsoIn={alsoIn}
              />
            ))}
            {g.planned ? (
              <div className="co-orbit-empty-copy" style={{ "--outline-depth": depth } as CSSProperties}>
                Not built yet. Nothing can be filed here until this group exists.
              </div>
            ) : kids.length === 0 && g.facts.length === 0 ? (
              <div className="co-orbit-empty-copy" style={{ "--outline-depth": depth } as CSSProperties}>
                Nothing charted yet.
              </div>
            ) : null}
            {!g.planned
              ? g.facts.map((fact, index) => {
                  const title = stripFactPrefix(fact.label, g.label);
                  const also = alsoIn(fact.id, g.key);
                  const active = activeFactId === fact.id;
                  const selected = fact.id === selectedFactId;
                  return (
                    <button
                      key={fact.id}
                      type="button"
                      tabIndex={descendantsRevealed ? 0 : -1}
                      aria-hidden={descendantsRevealed ? undefined : true}
                      aria-controls={selected ? UNIVERSE_FACT_CARD_ID : undefined}
                      aria-expanded={selected}
                      className="co-fact-line"
                      data-shown={descendantsRevealed || undefined}
                      data-fact-id={fact.id}
                      data-active={active || undefined}
                      data-selected={selected ? "true" : undefined}
                      onClick={() => onSelectFact(fact.id)}
                      onMouseEnter={() => onHoverFact(fact.id)}
                      onMouseLeave={() => onHoverFact(null)}
                      onFocus={() => onHoverFact(fact.id)}
                      onBlur={() => onHoverFact(null)}
                      style={
                        {
                          "--row-i": kids.length + index,
                          "--outline-depth": depth,
                        } as CSSProperties
                      }
                    >
                      {fact.kind === "new" ? (
                        <span aria-hidden className="co-kind-dot co-kind-dot--new" />
                      ) : (
                        <span aria-hidden className="co-kind-dot co-kind-dot--established" />
                      )}
                      <span className="co-fact-label" dir="auto">{title}</span>
                      {also ? <span className="co-fact-also">also in {also}</span> : null}
                    </button>
                  );
                })
              : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UniverseOutlineList({
  groups,
  openGroups,
  onToggleGroup,
  activeFactId,
  selectedFactId,
  onSelectFact,
  onHoverFact,
  litSubjectKey,
  onHoverSubject,
  alsoIn,
  arrival,
}: {
  /** Arrival of the top-level rows: CSS animation with its delay, or a plain fade. */
  arrival?: { base: number } | null;
  groups: UniverseOutlineGroup[];
  openGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  activeFactId: string | null;
  selectedFactId: string | null;
  onSelectFact: (id: string) => void;
  onHoverFact: (id: string | null) => void;
  litSubjectKey: string | null;
  onHoverSubject: (subjectKey: string | null) => void;
  alsoIn: (factId: string, groupKey: string) => string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {groups.map((g, gi) => (
        <GroupRow
          key={g.key}
          index={gi}
          arrival={arrival ? `coFadeIn .3s ease-out ${(arrival.base + gi * 0.05).toFixed(2)}s both` : undefined}
          g={g}
          depth={0}
          revealed
          openGroups={openGroups}
          onToggleGroup={onToggleGroup}
          activeFactId={activeFactId}
          selectedFactId={selectedFactId}
          onSelectFact={onSelectFact}
          onHoverFact={onHoverFact}
          litSubjectKey={litSubjectKey}
          onHoverSubject={onHoverSubject}
          alsoIn={alsoIn}
        />
      ))}
    </div>
  );
}
